import { useEffect, useState } from "react";
import {
  Typography, Grid, Button, Box, TextField, FormControl, Chip,
  MenuItem, Select, Divider, FormHelperText, IconButton,
  CircularProgress, Snackbar, Alert, Paper, InputAdornment
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LaptopIcon from '@mui/icons-material/Laptop';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { useSearchParams } from 'react-router-dom';
import PropTypes from "prop-types";
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';

import FileUploader from "../../upload/FileUpload";
import { getCategories, getSubcategories } from "../../../actions/categories";
import { isEmpty, areAllValuesEmpty, urlImg } from "../../common/helpers";
import { addProfileService, updateProfileService, deleteServiceImage } from "../../../actions/profile";
import random from "../../common/random";
import typeImg from "../../common/typeImg";
import SelectorModule from "../../common/SelectorModule";

import { useDispatch, useSelector } from "react-redux";

const FormServicio = ({ irA }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("service") || null;
  const servicios = useSelector(state => state.profile.profile.servicios);
  const categories = useSelector(state => state.cat.categories);
  const infoServicio = {
    nombre: "",
    descripcion: "",
    categoria: "",
    subcategoria: "",
    incluye: "",
    infoAdicional: "",
    ubicacion: {
      direccion: "",
      place_id: ""
    },
    tipoCosto: "",
    costoServicio: "",
    modalidades: [],
    idiomas: [],
    tags: [],
    duracionEstimada: "",
    unidadDuracion: "Horas",
    disponibilidad: [] // Structure: { day: 'Lunes', slots: [{inicio: '', fin: ''}] }
  }

  const [service, setService] = useState(infoServicio);
  const [errores, setErrores] = useState({});
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [subcategorias, setSubCat] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedDays, setExpandedDays] = useState([]);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    dispatch(getCategories());
  }, []);

  useEffect(() => {
    if (id) {
      const filtrado = servicios.filter(s => s._id === id);
      setService({
        nombre: filtrado[0]?.nombre || "",
        descripcion: filtrado[0]?.descripcion || "",
        incluye: filtrado[0]?.incluye || "",
        categoria: filtrado[0]?.categoria || "",
        subcategoria: filtrado[0]?.subcategoria || "",
        infoAdicional: filtrado[0]?.infoAdicional || "",
        ubicacion: filtrado[0]?.ubicacion || { direccion: "", place_id: "" },
        tipoCosto: filtrado[0]?.tipoCosto || "",
        costoServicio: filtrado[0]?.costoServicio || "",
        modalidades: filtrado[0]?.modalidades || [],
        idiomas: filtrado[0]?.idiomas || [],
        tags: filtrado[0]?.tags || [],
        duracionEstimada: filtrado[0]?.duracionEstimada || "",
        unidadDuracion: filtrado[0]?.unidadDuracion || "Horas",
        disponibilidad: filtrado[0]?.disponibilidad || []
      });
      if (filtrado[0]?.img) {
        setExistingImages(filtrado[0].img);
      }
    }
  }, [id, servicios]);

  useEffect(() => {
    if (service.categoria) {
      subCat(service.categoria);
    }
  }, [service.categoria]);

  const subCat = async cat => {
    const res = await getSubcategories(cat);
    setSubCat(res);
  }

  const handleChange = (field, value) => {
    setService(prev => ({
      ...prev,
      [field]: value
    }));
  }

  const handleDeleteImage = async (imgName) => {
    const success = await dispatch(deleteServiceImage(id, imgName));
    if (success) {
      setExistingImages(prev => prev.filter(img => img.nombre !== imgName));
    }
  }

  const daysOfWeek = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  // 'HH:MM' -> minutos del día.
  const aMinutos = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };

  // Un horario es inválido si la hora de fin no es posterior a la de inicio.
  // Excepción: 00:00 en "fin" se interpreta como medianoche (fin del día), el máximo permitido.
  const slotInvalido = (slot) => {
    if (!slot.inicio || !slot.fin) return false;
    const ini = aMinutos(slot.inicio);
    const fin = slot.fin === '00:00' ? 1440 : aMinutos(slot.fin);
    return fin <= ini;
  };

  // Etiqueta amigable 12h: '00:00' -> '12:00 am', '13:30' -> '1:30 pm'
  const etiquetaHora = (hhmm) => {
    const pad = (n) => n.toString().padStart(2, '0');
    const [h, m] = hhmm.split(':').map(Number);
    const periodo = h < 12 ? 'am' : 'pm';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${pad(m)} ${periodo}`;
  };

  // Opciones por hora en punto. Para 'fin' se omite 00:00 al inicio y se agrega
  // "12:00 am (medianoche)" al final como tope del día.
  const opcionesHora = (tipo) => {
    const pad = (n) => n.toString().padStart(2, '0');
    const arr = [];
    for (let h = tipo === 'fin' ? 1 : 0; h < 24; h++) {
      const v = `${pad(h)}:00`;
      arr.push({ value: v, label: etiquetaHora(v) });
    }
    if (tipo === 'fin') arr.push({ value: '00:00', label: '12:00 am (medianoche)' });
    return arr;
  };
  const opcionesInicio = opcionesHora('inicio');
  const opcionesFin = opcionesHora('fin');

  const toggleDay = (day) => {
    const current = [...service.disponibilidad];
    const index = current.findIndex(d => d.day === day);
    if (index > -1) {
      current.splice(index, 1);
      setExpandedDays(prev => prev.filter(d => d !== day));
    } else {
      current.push({ day, slots: [{ inicio: '', fin: '' }] });
      setExpandedDays(prev => [...prev, day]);
    }
    handleChange('disponibilidad', current);
  }

  const toggleExpandDay = (day) => {
    setExpandedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  }

  const handleAddTag = () => {
    if (tagInput.trim() !== "" && !service.tags.includes(tagInput.trim())) {
      setService(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput("");
    }
  }

  const handleRemoveTag = (tagToDelete) => {
    setService(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  }

  const addTimeSlot = (day) => {
    const current = [...service.disponibilidad];
    const index = current.findIndex(d => d.day === day);
    if (index > -1) {
      current[index].slots.push({ inicio: '', fin: '' });
      handleChange('disponibilidad', current);
    }
  }

  const removeTimeSlot = (day, slotIndex) => {
    const current = [...service.disponibilidad];
    const index = current.findIndex(d => d.day === day);
    if (index > -1) {
      current[index].slots.splice(slotIndex, 1);
      if (current[index].slots.length === 0) {
        current.splice(index, 1);
        setExpandedDays(prev => prev.filter(d => d !== day));
      }
      handleChange('disponibilidad', current);
    }
  }

  const updateTimeSlot = (day, slotIndex, field, value) => {
    const current = [...service.disponibilidad];
    const index = current.findIndex(d => d.day === day);
    if (index > -1) {
      current[index].slots[slotIndex][field] = value;
      handleChange('disponibilidad', current);
    }
  }

  const replicateDay = (day) => {
    const current = [...service.disponibilidad];
    const dayIndex = current.findIndex(d => d.day === day);
    if (dayIndex === -1) return;

    const currentSlots = [...current[dayIndex].slots];
    const dayInWeekIndex = daysOfWeek.indexOf(day);

    // Find the next selected day
    let nextSelectedDay = null;
    for (let i = 1; i < 7; i++) {
      const checkIndex = (dayInWeekIndex + i) % 7;
      const checkDay = daysOfWeek[checkIndex];
      if (current.some(d => d.day === checkDay)) {
        nextSelectedDay = checkDay;
        break;
      }
    }

    if (nextSelectedDay) {
      const nextDayIndex = current.findIndex(d => d.day === nextSelectedDay);
      current[nextDayIndex].slots = currentSlots.map(s => ({ ...s }));
      handleChange('disponibilidad', current);
      setAlert({ open: true, message: `Horarios replicados a ${nextSelectedDay}`, severity: 'success' });
    } else {
      setAlert({ open: true, message: "No hay otro día seleccionado para replicar", severity: 'warning' });
    }
  }

  const handleSubmit = async (e, borrador = false) => {
    e.preventDefault();
    if (loading) return;

    // Check if at least one meaningful field is filled for a draft
    const draftFields = [
      'nombre', 'descripcion', 'incluye', 'infoAdicional', 'costoServicio',
      'categoria', 'ubicacion', 'modalidades', 'idiomas', 'disponibilidad'
    ];

    const isAtLeastOneFieldFilled = draftFields.some(field => {
      const val = service[field];
      if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        return !areAllValuesEmpty(val);
      }
      return !isEmpty(val);
    }) || images.length > 0 || existingImages.length > 0;

    if (borrador && !isAtLeastOneFieldFilled) {
      return setAlert({
        open: true,
        message: "Completa al menos un campo para guardar el borrador",
        severity: "warning"
      });
    }

    const info = {
      nombre: service.nombre,
      descripcion: service.descripcion,
      incluye: service.incluye,
      infoAdicional: service.infoAdicional,
      categoria: service.categoria || null,
      subcategoria: service.subcategoria || null,
      costoServicio: service.costoServicio,
      tipoCosto: service.tipoCosto,
      ubicacion: service.ubicacion || null,
      modalidades: service.modalidades,
      idiomas: service.idiomas,
      duracionEstimada: service.duracionEstimada,
      unidadDuracion: service.unidadDuracion,
      disponibilidad: service.disponibilidad,
      tags: service.tags || [],
      borrador
    };

    if (!borrador) {
      let erroresEncontrados = {};

      // Mandatory fields according to UI indicators (*)
      const requiredFields = [
        'nombre', 'descripcion', 'incluye', 'categoria',
        'costoServicio', 'tipoCosto', 'modalidades', 'disponibilidad', 'ubicacion'
      ];

      requiredFields.forEach(field => {
        if (field === 'ubicacion') {
          if (info.modalidades.includes('Presencial') && isEmpty(info.ubicacion.direccion)) {
            erroresEncontrados.ubicacion = "Debes ingresar una ubicación";
          }
        } else if (field === 'disponibilidad') {
          if (isEmpty(info[field])) {
            erroresEncontrados[field] = "Este es un campo obligatorio";
          }
        } else if (isEmpty(info[field])) {
          erroresEncontrados[field] = "Este es un campo obligatorio";
        }
      });

      // Cada horario debe tener inicio y fin, y el fin debe ser posterior al inicio (máximo 12:00 am)
      if (!erroresEncontrados.disponibilidad && !isEmpty(info.disponibilidad)) {
        const horarioInvalido = info.disponibilidad.some(d =>
          (d.slots || []).some(s => !s.inicio || !s.fin || slotInvalido(s))
        );
        if (horarioInvalido) {
          erroresEncontrados.disponibilidad = "Revisa los horarios: completa inicio y fin, y que el fin sea posterior al inicio (máximo 12:00 am).";
        }
      }

      if (images.length + existingImages.length < 3) {
        erroresEncontrados.imagenes = "Debes tener al menos 3 imágenes para tu servicio";
      }

      if (!isEmpty(erroresEncontrados)) {
        setErrores(erroresEncontrados);
        setAlert({
          open: true,
          message: "Por favor, completa los campos obligatorios",
          severity: "error"
        });
        return;
      }
    } else {
      // Clear errors if saving as draft
      setErrores({});
    }

    setLoading(true);

    const imgFormData = new FormData();
    const nombresImg = [];

    if (images.length > 0) {
      images.forEach((img, i) => {
        const identificador = random(7);
        const nombre = identificador + typeImg(img);

        // Add to JSON for DB
        nombresImg.push({ nombre });

        // Add to FormData for Upload
        imgFormData.append('files', img);
        imgFormData.append('nombre', identificador);
      });
    }

    // Add img array to service info
    info.img = nombresImg;

    if (!id) {
      const res = await dispatch(addProfileService(info, imgFormData));
      if (res) {
        setLoading(false);
        irA("/user/servicios");
      } else {
        setLoading(false);
      }
    } else {
      const res = await dispatch(updateProfileService(info, id, imgFormData));
      if (res) {
        setLoading(false);
        irA("/user/servicios");
      } else {
        setLoading(false);
      }
    }
  }

  return (
    <Box>
      <Typography variant="h6" color="primary">
        Crear Servicio Independiente
      </Typography>
      <Typography
        variant="body2"
        color="#737791"
        sx={{ marginBottom: 3 }}
      >
        Completa la información de tu servicio para publicarlo
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item size={12}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Grid item>
                <Typography variant="body1" color="primary" fontWeight={600}>
                  Información Básica
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ mb: 3 }} />

            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Nombre del Servicio<span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              id="service"
              placeholder="Ej: Clase online de repostería casera"
              name="servicio"
              fullWidth
              value={service.nombre}
              onChange={(e) => handleChange("nombre", e.target.value)}
              error={!isEmpty(errores.nombre)}
              helperText={errores?.nombre}
            />
          </Grid>
          <Grid item size={12}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Grid item>
                <Typography variant="body2" color="primary" fontWeight={600}>
                  Descripción<span style={{ color: 'red' }}>*</span>
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="caption" color="textSecondary">
                  {service.descripcion.length}/250
                </Typography>
              </Grid>
            </Grid>
            <TextField
              placeholder="Describe brevemente el servicio"
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              autoComplete="off"
              value={service.descripcion}
              onChange={(e) => handleChange("descripcion", e.target.value.slice(0, 250))}
              error={!isEmpty(errores.descripcion)}
              helperText={errores?.descripcion}
            />
          </Grid>
          <Grid item size={12}>
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Lo que incluye<span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              placeholder="Ej: Material de trabajo, certificado, seguimiento post-servicio"
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              autoComplete="off"
              value={service.incluye}
              onChange={(e) => handleChange("incluye", e.target.value)}
              error={!isEmpty(errores.incluye)}
              helperText={errores?.incluye}
            />
          </Grid>
          <Grid item size={12}>
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Información Adicional Relevante
            </Typography>
            <TextField
              name="info"
              placeholder="Requisitos previos, preparación necesaria, etc."
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              autoComplete="off"
              value={service.infoAdicional}
              onChange={(e) => handleChange("infoAdicional", e.target.value)}
              error={!isEmpty(errores.infoAdicional)}
              helperText={errores?.infoAdicional}
            />
          </Grid>

          <Grid item size={12} mb={2}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 2, mb: 1 }}>
              <Grid item>
                <Typography variant="body1" color="primary" fontWeight={600}>
                  Fotos del Servicio
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ mb: 3 }} />

            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Grid item>
                <Typography variant="body2" color="primary" fontWeight={600}>
                  Fotos del Servicio<span style={{ color: 'red' }}>*</span>
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="caption">
                  {(images.length + existingImages.length)}/10 (3 minimo)
                </Typography>
              </Grid>
            </Grid>

            <FileUploader
              files={images}
              setFiles={setImages}
              multiple
            />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Por favor sube al menos 3 imágenes
            </Typography>
            {
              !isEmpty(errores.imagenes) && (
                <FormHelperText error sx={{ textAlign: "center" }}>
                  {errores.imagenes}
                </FormHelperText>
              )
            }
            {
              !isEmpty(existingImages) && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {existingImages.map((img, i) => (
                    <Box key={i} sx={{ position: 'relative' }}>
                      <img
                        src={`${urlImg}/servicios/${id}/${img.nombre}`}
                        alt="Servicio"
                        style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteImage(img.nombre)}
                        sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
                      >
                        <DeleteIcon color="error" fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )
            }
          </Grid>

          <Grid item size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Categorización
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
                  Categoría<span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl
                  fullWidth
                  error={!isEmpty(errores.categoria)}
                >
                  <Select
                    value={service.categoria}
                    onChange={(e) => handleChange("categoria", e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Selecciona la categoría</em>
                    </MenuItem>
                    {
                      categories?.map(cat => (
                        <MenuItem
                          key={cat._id}
                          value={cat._id}
                        >
                          {cat.name}
                        </MenuItem>
                      ))
                    }
                  </Select>
                  {
                    !isEmpty(errores.categoria) && (
                      <FormHelperText>
                        {errores?.categoria}
                      </FormHelperText>
                    )
                  }
                </FormControl>
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
                  Subcategoría (Opcional)
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={service.subcategoria}
                    onChange={(e) => handleChange("subcategoria", e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Selecciona</em>
                    </MenuItem>
                    {
                      subcategorias?.map(sub => (
                        <MenuItem
                          key={sub._id}
                          value={sub._id}
                        >
                          {sub.name}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Aviso: la categoría elegida exige cédula profesional aprobada */}
            {(() => {
              const catSel = categories?.find(c => String(c._id) === String(service.categoria));
              if (!catSel?.requiereCedula) return null;
              return (
                <Alert severity="warning" icon={<WorkspacePremiumOutlinedIcon />}
                  sx={{ mt: 2, borderRadius: 2, fontWeight: 500, alignItems: 'flex-start' }}>
                  <b>Esta categoría requiere cédula profesional.</b> Podrás crear el servicio,
                  pero <b>no se publicará ni se podrá contratar</b> hasta que subas tu cédula de
                  «{catSel.name}» y nuestro equipo la apruebe. Súbela en{' '}
                  <b>Mi perfil → Documentos y verificación</b>.
                </Alert>
              );
            })()}

            {/* Tags Section */}
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
              Etiquetas / Palabras Clave
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  placeholder="Escribe una etiqueta"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography color="primary" sx={{ fontWeight: 'bold' }}>#</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleAddTag}
                  sx={{ height: '56px', fontWeight: 600, textTransform: 'none' }}
                >
                  Agregar
                </Button>
              </Grid>
              <Grid item size={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {service.tags && service.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      color="primary"
                      variant="outlined"
                      sx={{ borderRadius: '8px', fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Modalidad del Servicio
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body2" sx={{ mb: 2 }}>
              Selecciona las modalidades disponibles<span style={{ color: 'red' }}>*</span>
            </Typography>

            <SelectorModule
              options={[
                { id: 'Presencial', title: 'Presencial', desc: 'En un sitio específico', icon: <LocationOnIcon fontSize="large" color={service.modalidades.includes('Presencial') ? 'primary' : 'inherit'} /> },
                { id: 'Remoto', title: 'Remoto', desc: 'En línea / Virtual', icon: <LaptopIcon fontSize="large" color={service.modalidades.includes('Remoto') ? 'primary' : 'inherit'} /> },
                { id: 'Domicilio', title: 'A Domicilio', desc: 'En casa del cliente', icon: <HomeIcon fontSize="large" color={service.modalidades.includes('Domicilio') ? 'primary' : 'inherit'} /> }
              ]}
              value={service.modalidades}
              onChange={(val) => handleChange('modalidades', val)}
              multiple
            />
            {errores.modalidades && (
              <FormHelperText error sx={{ mt: 1 }}>{errores.modalidades}</FormHelperText>
            )}

            {service.modalidades.includes('Presencial') && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
                  Ubicación del Servicio (Google Maps)<span style={{ color: 'red' }}>*</span>
                </Typography>
                <GooglePlacesAutocomplete
                  autocompletionRequest={{
                    componentRestrictions: {
                      country: ['mx'],
                    }
                  }}
                  selectProps={{
                    value: service.ubicacion.direccion ? { label: service.ubicacion.direccion, value: service.ubicacion.place_id } : null,
                    onChange: (place) => {
                      if (place) {
                        handleChange('ubicacion', {
                          direccion: place.label,
                          place_id: place.value.place_id
                        });
                        if (errores.ubicacion) {
                          setErrores(prev => ({ ...prev, ubicacion: null }));
                        }
                      } else {
                        handleChange('ubicacion', { direccion: '', place_id: '' });
                      }
                    },
                    placeholder: "Busca tu dirección...",
                    styles: {
                      input: (provided) => ({
                        ...provided,
                        height: 48,
                      }),
                      control: (provided) => ({
                        ...provided,
                        borderColor: errores.ubicacion ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)',
                        '&:hover': {
                          borderColor: errores.ubicacion ? '#d32f2f' : 'rgba(0, 0, 0, 0.87)',
                        }
                      }),
                    },
                  }}
                />
                {errores.ubicacion && (
                  <FormHelperText error sx={{ mt: 1 }}>{errores.ubicacion}</FormHelperText>
                )}
              </Box>
            )}
          </Grid>

          <Grid item size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Idiomas disponibles
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body2" sx={{ mb: 2 }}>
              Selecciona los idiomas en los que podrá ser impartido el servicio
            </Typography>

            <SelectorModule
              options={[
                { id: 'espanol', title: 'Español', icon: '🇪🇸' },
                { id: 'english', title: 'English', icon: '🇺🇸' },
                { id: 'francais', title: 'Français', icon: '🇫🇷' }
              ]}
              value={service.idiomas}
              onChange={(val) => handleChange('idiomas', val)}
              multiple
            />
          </Grid>
        </Grid>

        <Divider />

        <Grid container spacing={3} sx={{ mt: 3, mb: 4 }}>
          <Grid item size={12}>
            <Typography variant="body1" color="primary" fontWeight={600}>
              Información Específicia
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          <Grid item size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Unidad de Cobro<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Grid container spacing={1}>
              {[
                { id: 'Servicio', title: 'Servicio', icon: <AttachMoneyIcon /> },
                { id: 'Hora', title: 'Hora', icon: <AccessTimeIcon /> },
                { id: 'Día', title: 'Día', icon: <CalendarTodayIcon /> }
              ].map((unit) => (
                <Grid item size={4} key={unit.id}>
                  <Paper
                    elevation={0}
                    onClick={() => handleChange('tipoCosto', unit.id)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      border: '1px solid',
                      borderColor: service.tipoCosto === unit.id ? 'primary.main' : '#E0E0E0',
                      borderRadius: '8px',
                      bgcolor: service.tipoCosto === unit.id ? 'rgba(14, 36, 101, 0.04)' : 'transparent',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(14, 36, 101, 0.02)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', color: service.tipoCosto === unit.id ? 'primary.main' : '#737791' }}>
                      {unit.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={service.tipoCosto === unit.id ? 'primary' : 'inherit'}
                    >
                      {unit.title}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            {errores.tipoCosto && <FormHelperText error>{errores.tipoCosto}</FormHelperText>}
          </Grid>

          <Grid item size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Costo desde (servicio):<span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              name="costo"
              placeholder="2"
              variant="outlined"
              fullWidth
              autoComplete="off"
              value={service.costoServicio}
              type="number"
              onChange={(e) => handleChange("costoServicio", e.target.value)}
              error={!isEmpty(errores.costoServicio)}
              helperText={errores?.costoServicio}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span style={{ color: '#0e2465' }}>$</span>
                  </InputAdornment>
                ),
              }}
            />
            {/* Desglose de la comisión: que el proveedor fije su precio sabiendo su neto */}
            {parseFloat(service.costoServicio) > 0 && (() => {
              const costo = parseFloat(service.costoServicio);
              const comision = Math.round(costo * 10) / 100;
              const neto = Math.round((costo - comision) * 100) / 100;
              const unidad = service.tipoCosto ? ` por ${service.tipoCosto.toLowerCase()}` : '';
              return (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#1B7A5A' }}>
                  Recibirás <strong>${neto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong>{unidad} —
                  la plataforma retiene el 10% de comisión (${comision.toLocaleString('es-MX', { minimumFractionDigits: 2 })}).
                </Typography>
              );
            })()}
          </Grid>

          <Grid item size={12}>
            <Divider sx={{ my: 1, opacity: 0.5 }} />
          </Grid>

          <Grid item size={12}>
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Duración Estimada <span style={{ fontWeight: 400, color: '#737791' }}>(opcional)</span>
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item size={{ xs: 8, md: 4 }}>
                <TextField
                  placeholder="Ej: 2"
                  fullWidth
                  value={service.duracionEstimada}
                  onChange={(e) => handleChange("duracionEstimada", e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTimeIcon fontSize="small" sx={{ color: '#737791' }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 4, md: 2 }}>
                <Select
                  fullWidth
                  value={service.unidadDuracion}
                  onChange={(e) => handleChange("unidadDuracion", e.target.value)}
                >
                  <MenuItem value="Horas">Horas</MenuItem>
                  <MenuItem value="Días">Días</MenuItem>
                  <MenuItem value="Semanas">Semanas</MenuItem>
                </Select>
              </Grid>
            </Grid>
            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#737791' }}>
              Indica cuánto tiempo toma completar el servicio (si aplica)
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
          Disponibilidad
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Typography variant="body2" color={errores.disponibilidad ? "error" : "primary"} fontWeight={600} sx={{ mb: 1 }}>
          Disponibilidad Semanal<span style={{ color: 'red' }}>*</span>
        </Typography>
        {errores.disponibilidad && (
          <FormHelperText error sx={{ mb: 1 }}>{errores.disponibilidad}</FormHelperText>
        )}
        <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
          {service.disponibilidad.length === 0 ? "Selecciona al menos un día" : `${service.disponibilidad.length} días seleccionados`}
        </Typography>
        <Typography variant="caption" sx={{ color: '#737791', mb: 2, display: 'block' }}>
          La hora de <b>fin</b> debe ser posterior a la de <b>inicio</b>. El máximo es <b>12:00 am</b> (medianoche); no se permiten horarios que crucen al día siguiente.
        </Typography>

        <Grid container spacing={1} sx={{ mb: 3 }}>
          {daysOfWeek.map((day) => {
            const isSelected = service.disponibilidad.some(d => d.day === day);
            return (
              <Grid item size={{ xs: 6, sm: 3, md: 1.7 }} key={day}>
                <Paper
                  elevation={0}
                  onClick={() => toggleDay(day)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : '#E0E0E0',
                    borderRadius: '8px',
                    bgcolor: isSelected ? 'rgba(14, 36, 101, 0.04)' : 'transparent',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(14, 36, 101, 0.02)'
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={isSelected ? 'primary' : 'inherit'}
                  >
                    {day}
                  </Typography>
                </Paper>
              </Grid>
            )
          })}
        </Grid>

        <Box sx={{ mb: 4 }}>
          {service.disponibilidad.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 6,
                textAlign: 'center',
                bgcolor: 'transparent',
                borderStyle: 'dashed'
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 48, color: '#E0E0E0', mb: 2 }} />
              <Typography variant="body2">
                Selecciona los días de disponibilidad arriba
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {daysOfWeek.filter(day => service.disponibilidad.some(d => d.day === day)).map((day) => {
                const dayData = service.disponibilidad.find(d => d.day === day);
                const isExpanded = expandedDays.includes(day);
                return (
                  <Paper
                    key={day}
                    variant="outlined"
                    sx={{
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      onClick={() => toggleExpandDay(day)}
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        bgcolor: isExpanded ? 'rgba(14, 36, 101, 0.02)' : 'transparent'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AccessTimeIcon color="primary" />
                        <Box>
                          <Typography variant="body1" fontWeight={600} color="primary">
                            {day}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {dayData.slots.length} {dayData.slots.length === 1 ? 'horario' : 'horarios'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: '#737791' }}>
                          {isExpanded ? 'Ocultar' : 'Configurar'}
                        </Typography>
                        {isExpanded ? <ExpandLessIcon sx={{ color: '#737791' }} /> : <ExpandMoreIcon sx={{ color: '#737791' }} />}
                      </Box>
                    </Box>

                    {isExpanded && (
                      <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                          {dayData.slots.map((slot, sIdx) => {
                            const invalido = slotInvalido(slot);
                            return (
                            <Box key={sIdx}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item sx={{ display: { xs: 'none', sm: 'block' } }}>
                                <Typography variant="body2" fontWeight={600} sx={{ color: '#737791' }}>
                                  {sIdx + 1}
                                </Typography>
                              </Grid>
                              <Grid item size={{ xs: 5, sm: 4.5 }}>
                                <Typography variant="caption" sx={{ ml: 0.5, mb: 0.5, display: 'block' }}>Inicio</Typography>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  error={invalido}
                                  value={slot.inicio}
                                  onChange={(e) => updateTimeSlot(day, sIdx, 'inicio', e.target.value)}
                                >
                                  {opcionesInicio.map(o => (
                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item size={{ xs: 5, sm: 4.5 }}>
                                <Typography variant="caption" sx={{ ml: 0.5, mb: 0.5, display: 'block' }}>Fin</Typography>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  error={invalido}
                                  value={slot.fin}
                                  onChange={(e) => updateTimeSlot(day, sIdx, 'fin', e.target.value)}
                                >
                                  {opcionesFin.map(o => (
                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item size={{ xs: 2, sm: 1.5 }} sx={{ mt: 2.5 }}>
                                <IconButton
                                  onClick={() => removeTimeSlot(day, sIdx)}
                                  sx={{
                                    bgcolor: 'rgba(255, 76, 97, 0.1)',
                                    color: 'error.main',
                                    borderRadius: '8px',
                                    '&:hover': { bgcolor: 'rgba(255, 76, 97, 0.2)' }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Grid>
                            </Grid>
                            {invalido && (
                              <FormHelperText error sx={{ ml: 0.5, mt: 0.5 }}>
                                La hora de fin debe ser posterior a la de inicio (máximo 12:00 am).
                              </FormHelperText>
                            )}
                            </Box>
                            );
                          })}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => addTimeSlot(day)}
                            sx={{ borderRadius: '8px', textTransform: 'none' }}
                          >
                            Agregar horario
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => replicateDay(day)}
                            sx={{ borderRadius: '8px', textTransform: 'none' }}
                          >
                            Replicar al día siguiente
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                )
              })}
            </Box>
          )}
        </Box>

        <Divider />

        <Grid container spacing={2} mt={3} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={e => handleSubmit(e, true)}
            type="button"
            disabled={loading}
          >
            Guardar como borrador
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => irA("/user/servicios")}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? "Procesando..." : "Publicar servicio"}
          </Button>
        </Grid>
      </form>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

FormServicio.propTypes = {
  irA: PropTypes.func.isRequired,
}

export default FormServicio;
